import React, { useState } from 'react';
import {
  BarChart3, TrendingUp, TrendingDown, Users, DollarSign, Clock,
  Target, Award, Star, Calendar, Filter, Download, RefreshCw,
  Eye, MessageSquare, CheckCircle, AlertCircle, Zap, Heart,
  Leaf, Shield, Globe, ArrowUpRight, ArrowDownRight, Plus,
  Briefcase, UserCheck, Timer, Activity, PieChart, LineChart
} from 'lucide-react';

const Analytics = ({ company }) => {
  const [timeRange, setTimeRange] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('overview');

  // Mock data pour les analytics
  const analyticsData = {
    overview: {
      week: {
        totalProjects: 5,
        totalBudget: 18500,
        newApplications: 28,
        responseRate: 85,
        averageMatchScore: 92,
        sustainabilityImpact: 96
      },
      month: {
        totalProjects: 18,
        totalBudget: 67200,
        newApplications: 156,
        responseRate: 89,
        averageMatchScore: 94,
        sustainabilityImpact: 98
      },
      quarter: {
        totalProjects: 47,
        totalBudget: 185000,
        newApplications: 432,
        responseRate: 91,
        averageMatchScore: 93,
        sustainabilityImpact: 97
      }
    },
    trends: [
      { period: 'Jan', projects: 12, budget: 45000, applications: 89, satisfaction: 4.7 },
      { period: 'Fév', projects: 18, budget: 52000, applications: 124, satisfaction: 4.8 },
      { period: 'Mar', projects: 15, budget: 48000, applications: 98, satisfaction: 4.9 },
      { period: 'Avr', projects: 22, budget: 63000, applications: 167, satisfaction: 4.8 },
      { period: 'Mai', projects: 19, budget: 58000, applications: 145, satisfaction: 4.9 },
      { period: 'Juin', projects: 25, budget: 72000, applications: 189, satisfaction: 4.9 }
    ],
    topFreelances: [
      { name: "Camille Dupont", projects: 8, revenue: 24500, rating: 4.9, speciality: "UX/UI Design" },
      { name: "Thomas Bernard", projects: 12, revenue: 36000, rating: 4.8, speciality: "Développement" },
      { name: "Sophie Martin", projects: 6, revenue: 19200, rating: 4.9, speciality: "Data Science" },
      { name: "Marie Laurent", projects: 9, revenue: 28800, rating: 4.7, speciality: "Marketing" },
      { name: "Lucas Moreau", projects: 7, revenue: 21000, rating: 4.8, speciality: "Consulting" }
    ],
    projectCategories: [
      { category: "Design", count: 15, budget: 48000, color: "text-blue-600", bg: "bg-blue-100" },
      { category: "Développement", count: 12, budget: 67200, color: "text-green-600", bg: "bg-green-100" },
      { category: "Marketing", count: 8, budget: 28500, color: "text-purple-600", bg: "bg-purple-100" },
      { category: "Data Science", count: 6, budget: 24800, color: "text-yellow-600", bg: "bg-yellow-100" },
      { category: "Consulting", count: 6, budget: 16500, color: "text-red-600", bg: "bg-red-100" }
    ],
    sustainabilityMetrics: {
      carbonReduction: 45,
      certifiedFreelances: 89,
      ethicalProjects: 98,
      socialImpact: 87,
      sustainableGoals: [
        { goal: "Neutralité carbone 2025", progress: 68, color: "green" },
        { goal: "50% freelances femmes", progress: 42, color: "purple" },
        { goal: "100% projets éthiques", progress: 98, color: "blue" },
        { goal: "Formation continue", progress: 76, color: "orange" }
      ]
    }
  };

  const currentData = analyticsData.overview[timeRange];

  const StatCard = ({ title, value, change, trend, icon: Icon, color, prefix = "", suffix = "" }) => (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-lg transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 ${color} rounded-2xl flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className={`flex items-center space-x-1 text-sm font-semibold ${
          trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-500' : 'text-gray-500'
        }`}>
          {trend === 'up' && <ArrowUpRight className="w-4 h-4" />}
          {trend === 'down' && <ArrowDownRight className="w-4 h-4" />}
          {change !== undefined && `${change > 0 ? '+' : ''}${change}%`}
        </div>
      </div>
      <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
      <p className="text-2xl font-bold text-gray-900">
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </p>
    </div>
  );

  const TrendChart = () => (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tendances des Projets</h3>
          <p className="text-sm text-gray-600">Évolution mensuelle de votre activité</p>
        </div>
        <div className="flex space-x-2">
          <button className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-lg">Projets</button>
          <button className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg">Budget</button>
          <button className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg">Candidatures</button>
        </div>
      </div>
      
      <div className="h-64 flex items-end justify-between space-x-2">
        {analyticsData.trends.map((item, index) => (
          <div key={index} className="flex-1 flex flex-col items-center">
            <div className="w-full bg-gray-100 rounded-t-lg relative overflow-hidden" style={{ height: '200px' }}>
              <div 
                className="absolute bottom-0 w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg transition-all duration-1000 delay-200"
                style={{ height: `${(item.projects / 25) * 100}%` }}
              ></div>
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 text-xs font-bold text-gray-700">
                {item.projects}
              </div>
            </div>
            <div className="mt-2 text-xs font-medium text-gray-600">{item.period}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const TopFreelancesTable = () => (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Top Freelances</h3>
          <p className="text-sm text-gray-600">Vos collaborateurs les plus performants</p>
        </div>
        <button className="text-green-600 hover:text-green-700 font-medium text-sm">
          Voir tout
        </button>
      </div>
      
      <div className="space-y-4">
        {analyticsData.topFreelances.map((freelance, index) => (
          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center text-white font-bold">
                {freelance.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">{freelance.name}</h4>
                <p className="text-sm text-gray-600">{freelance.speciality}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-lg font-bold text-gray-900">{freelance.projects}</div>
                <div className="text-xs text-gray-500">Projets</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-600">{freelance.revenue.toLocaleString()}€</div>
                <div className="text-xs text-gray-500">Revenus</div>
              </div>
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-bold text-gray-900">{freelance.rating}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const ProjectCategoriesChart = () => (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Répartition par Catégorie</h3>
          <p className="text-sm text-gray-600">Distribution de vos projets</p>
        </div>
      </div>
      
      <div className="space-y-4">
        {analyticsData.projectCategories.map((category, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 ${category.bg} rounded`}></div>
                <span className="font-medium text-gray-900">{category.category}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-gray-900">{category.count} projets</div>
                <div className="text-xs text-gray-500">{category.budget.toLocaleString()}€</div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${category.bg.replace('bg-', 'bg-').replace('-100', '-500')}`}
                style={{ width: `${(category.count / 15) * 100}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const SustainabilityDashboard = () => (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Leaf className="w-5 h-5 mr-2 text-green-500" />
            Impact Durabilité
          </h3>
          <p className="text-sm text-gray-600">Vos contributions aux objectifs RSE</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-green-600">Score: 98/100</div>
          <div className="text-xs text-green-700">Excellent impact</div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-white rounded-xl border border-green-200">
          <div className="text-2xl font-bold text-green-600 mb-1">-{analyticsData.sustainabilityMetrics.carbonReduction}%</div>
          <div className="text-xs text-gray-600">Émissions CO2</div>
        </div>
        <div className="text-center p-4 bg-white rounded-xl border border-green-200">
          <div className="text-2xl font-bold text-blue-600 mb-1">{analyticsData.sustainabilityMetrics.certifiedFreelances}%</div>
          <div className="text-xs text-gray-600">Freelances certifiés</div>
        </div>
        <div className="text-center p-4 bg-white rounded-xl border border-green-200">
          <div className="text-2xl font-bold text-purple-600 mb-1">{analyticsData.sustainabilityMetrics.ethicalProjects}%</div>
          <div className="text-xs text-gray-600">Projets éthiques</div>
        </div>
        <div className="text-center p-4 bg-white rounded-xl border border-green-200">
          <div className="text-2xl font-bold text-orange-600 mb-1">{analyticsData.sustainabilityMetrics.socialImpact}%</div>
          <div className="text-xs text-gray-600">Impact social</div>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="font-semibold text-gray-900">Objectifs de Développement Durable</h4>
        {analyticsData.sustainabilityMetrics.sustainableGoals.map((goal, index) => (
          <div key={index} className="bg-white rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">{goal.goal}</span>
              <span className="text-sm font-bold text-gray-700">{goal.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-1000 ${
                  goal.color === 'green' ? 'bg-green-500' :
                  goal.color === 'purple' ? 'bg-purple-500' :
                  goal.color === 'blue' ? 'bg-blue-500' : 'bg-orange-500'
                }`}
                style={{ width: `${goal.progress}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <BarChart3 className="w-8 h-8 mr-3 text-green-500" />
            Analytics & Performance
          </h1>
          <p className="text-gray-600">
            Analysez vos performances et l'impact de vos collaborations responsables.
          </p>
        </div>
        
        <div className="mt-4 lg:mt-0 flex items-center space-x-4">
          <select 
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="quarter">Ce trimestre</option>
          </select>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200">
            <Download className="w-4 h-4 text-gray-600" />
            <span className="text-gray-700">Exporter</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors duration-200">
            <RefreshCw className="w-4 h-4" />
            <span>Actualiser</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
        <StatCard
          title="Projets Totaux"
          value={currentData.totalProjects}
          change={12}
          trend="up"
          icon={Briefcase}
          color="bg-gradient-to-r from-blue-500 to-cyan-500"
        />
        <StatCard
          title="Budget Total"
          value={currentData.totalBudget}
          change={8}
          trend="up"
          icon={DollarSign}
          color="bg-gradient-to-r from-green-500 to-emerald-500"
          suffix="€"
        />
        <StatCard
          title="Candidatures"
          value={currentData.newApplications}
          change={23}
          trend="up"
          icon={Users}
          color="bg-gradient-to-r from-purple-500 to-pink-500"
        />
        <StatCard
          title="Taux de Réponse"
          value={currentData.responseRate}
          change={5}
          trend="up"
          icon={MessageSquare}
          color="bg-gradient-to-r from-orange-500 to-yellow-500"
          suffix="%"
        />
        <StatCard
          title="Score Match IA"
          value={currentData.averageMatchScore}
          change={2}
          trend="up"
          icon={Zap}
          color="bg-gradient-to-r from-indigo-500 to-purple-500"
          suffix="%"
        />
        <StatCard
          title="Impact Durable"
          value={currentData.sustainabilityImpact}
          change={1}
          trend="up"
          icon={Leaf}
          color="bg-gradient-to-r from-emerald-500 to-teal-500"
          suffix="%"
        />
      </div>

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Charts */}
        <div className="lg:col-span-2 space-y-8">
          <TrendChart />
          <ProjectCategoriesChart />
        </div>

        {/* Right Column - Tables & Info */}
        <div className="space-y-8">
          <TopFreelancesTable />
          
          {/* Quick Stats */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques Rapides</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Eye className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium text-gray-700">Profil vu</span>
                </div>
                <span className="text-sm font-bold text-blue-600">2,847 fois</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-gray-700">Taux de succès</span>
                </div>
                <span className="text-sm font-bold text-green-600">96%</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Timer className="w-5 h-5 text-purple-500" />
                  <span className="text-sm font-medium text-gray-700">Temps moyen</span>
                </div>
                <span className="text-sm font-bold text-purple-600">5.2 jours</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm font-medium text-gray-700">Note moyenne</span>
                </div>
                <span className="text-sm font-bold text-yellow-600">4.8/5</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sustainability Dashboard */}
      <SustainabilityDashboard />

      {/* Insights & Recommendations */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-100 p-6">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 mb-2">Insights IA & Recommandations</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="flex items-center space-x-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium text-gray-700">Tendance positive</span>
                </div>
                <p className="text-xs text-gray-600">Vos collaborations s'améliorent de 15% ce mois-ci</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium text-gray-700">Optimisation</span>
                </div>
                <p className="text-xs text-gray-600">Publiez vos projets le mardi pour +40% de candidatures</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Award className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium text-gray-700">Certification</span>
                </div>
                <p className="text-xs text-gray-600">Éligible au label "Entreprise Responsable Plus"</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors duration-200 text-sm">
                Voir toutes les recommandations
              </button>
              <button className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors duration-200 text-sm">
                Planifier une optimisation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;